import { useEffect, useRef } from 'react';
import { type CartItemReference } from '../models';

interface StockAvailabilityMap {
    [sku: string]: {
        isSalable: boolean;
        loading: boolean;
    };
}

interface UseAutoUnselectOutOfStockItemsParams {
    cartItemReferences: CartItemReference[];
    stockAvailabilityMap: StockAvailabilityMap;
    onAutoUnselect?: (params: { cartItemReference: CartItemReference; reason: 'out_of_stock' }) => void;
}

/**
 * AUTO-UNSELECT OUT-OF-STOCK ITEMS HOOK
 *
 * Business Logic:
 * - When user has selected items for checkout
 * - And stock check reveals item is out of stock
 * - Automatically unselect that item to prevent checkout errors
 * - Notify user about the auto-unselect action
 *
 * UX Benefits:
 * - Prevents user from attempting checkout with unavailable items
 * - Clear indication of which items have stock issues
 * - Reduces checkout friction and error states
 * - Proactive rather than reactive error handling
 *
 * @param cartItemReferences - All cart item references
 * @param stockAvailabilityMap - Stock data from batch query
 * @param onAutoUnselect - Callback when item is auto-unselected
 */
export const useAutoUnselectOutOfStockItems = ({
    cartItemReferences,
    stockAvailabilityMap,
    onAutoUnselect,
}: UseAutoUnselectOutOfStockItemsParams): void => {
    // Track which items we've already processed to avoid duplicate unselects
    const processedItemsRef = useRef<Map<string, boolean>>(new Map());

    useEffect(() => {
        // Check each selected item for stock availability
        cartItemReferences.forEach(cartItemReference => {
            // Only process selected items
            if (!cartItemReference.isSelected) {
                return;
            }

            // Skip if we've already processed this item
            const itemKey = cartItemReference.cartItemId || (cartItemReference as { localId?: string }).localId;
            if (!itemKey) {
                return;
            }

            // Check stock for the cart item in this reference
            const hasOutOfStockItem = (() => {
                const item = cartItemReference.cartItem;
                if (!item) return false;

                // Extract SKU based on item type
                const typename = (item as { __typename?: string })?.__typename;
                const sku =
                    typename === 'ConfigurableCartItem'
                        ? (item as { configured_variant?: { sku?: string } })?.configured_variant?.sku
                        : item?.product?.sku;

                if (!sku) {
                    return false;
                }

                const stockData = stockAvailabilityMap[sku];

                // If stock data is still loading, don't auto-unselect yet
                if (!stockData || stockData.loading) {
                    return false;
                }

                // Check if item is not salable
                return !stockData.isSalable;
            })();

            const wasOutOfStock = processedItemsRef.current.get(itemKey) === true;

            if (hasOutOfStockItem && !wasOutOfStock) {
                processedItemsRef.current.set(itemKey, true);

                // Trigger auto-unselect callback
                onAutoUnselect?.({
                    cartItemReference,
                    reason: 'out_of_stock',
                });
            }

            if (!hasOutOfStockItem && wasOutOfStock) {
                processedItemsRef.current.delete(itemKey);
            }
        });
    }, [cartItemReferences, stockAvailabilityMap, onAutoUnselect]);

    // Cleanup processed items when cart changes significantly
    useEffect(() => {
        // Reset processed items if cart references change
        const currentItemKeys = new Set(
            cartItemReferences
                .map(ref => ref.cartItemId || (ref as { localId?: string }).localId)
                .filter((key): key is string => !!key)
        );

        // Remove processed items that are no longer in cart
        const processedItems = Array.from(processedItemsRef.current.keys());
        processedItems.forEach(itemKey => {
            if (!currentItemKeys.has(itemKey)) {
                processedItemsRef.current.delete(itemKey);
            }
        });
    }, [cartItemReferences]);
};
